import React, { FC, useMemo, useRef, useState } from 'react';

import { createPortal } from 'react-dom';
import { FormattedMessage, useIntl } from 'react-intl';

import HeadphonesIcon from '@mui/icons-material/Headphones';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import {
    BaseStyleEnums,
    CoreUtils,
    ShortCodeEnums,
    GameClock,
    GameClockStyles,
    TimeZoneEnums,
} from '@nhl/nhl-breakaway-ui';

import CommonMessages from '~/core/commonMessages';
import { TicketsIcon } from '~/core/customIcons';
import { PageName } from '~/core/enums';
import { useOptionForLocale } from '~/core/hooks/useOptionForLocale';
import { useSizeWithThrottle } from '~/core/hooks/useSizeWithThrottle';
import { showTicketsLink } from '~/core/utils';
import { defaultLocationData, useLocationQuery } from '~/redux/ducks/location';
import { useGetWebStreamURLQuery } from '~/redux/ducks/webStreamURL';
import GameOrSeriesScoreboard from '~/views/components/gameOrSeriesScoreboard/GameOrSeriesScoreboard';
import { formatDate } from '~/views/components/gameOrSeriesScoreboard/utils';
import TvBroadcast from '~/views/components/tvBroadcast/TvBroadcast';
import { CommonGCGame } from '~/views/pages/gameCenter/types';

import RadioPlayer from './components/radioPlayer/RadioPlayer';
import SpecialEventDisplay from './components/specialEventDisplay/SpecialEventDisplay';

import {
    DateContainer,
    DateNetworkContainer,
    GameInfo,
    LinkContainer,
    LinksContainer,
    NetworkContainer,
    PeriodTimeContainer,
    ScoreboardButton,
    ScoreBox,
    ScoreContainer,
    SituationDescription,
} from './styles';

type ScoreboardProps = {
    game: CommonGCGame;
};

const Scoreboard: FC<ScoreboardProps> = ({ game }) => {
    const intl = useIntl();
    const { data: countryCode = defaultLocationData } = useLocationQuery({});

    const isLiveGame = CoreUtils.isGameLive(game.gameState);
    const postGame = CoreUtils.isPostGame(game.gameState);

    const size = useSizeWithThrottle();
    const isMedium = size.width >= BaseStyleEnums.ScreenWidthBreakpoints.medium;

    const dateContainerRef = useRef<HTMLDivElement>(null);
    const linkContainerRef = useRef<HTMLDivElement>(null);

    const { data: streamUrl } = useGetWebStreamURLQuery({
        broadcasts: game.tvBroadcasts,
        countryCode,
        language: intl.locale as ShortCodeEnums.Language,
    });

    const ticketsLink = useOptionForLocale({
        [ShortCodeEnums.Language.English]: game.ticketsLink,
        [ShortCodeEnums.Language.French]: game.ticketsLinkFr,
    });

    const [isRadioPlayerOpen, setRadioPlayerOpen] = useState(false);

    const linkContainerProps = {
        noLinks: postGame || !showTicketsLink(game),
        twoLinks: isLiveGame && CoreUtils.isGameActive(game.gameState) && streamUrl !== undefined,
        linksWidth: linkContainerRef.current?.getBoundingClientRect().width,
        dateWidth: dateContainerRef.current?.getBoundingClientRect().width,
    };

    const gameHasRadioStreams = useMemo<boolean>(
        () =>
            game.homeTeam.radioLink !== undefined ||
            game.homeTeam.radioLinkFr !== undefined ||
            game.awayTeam.radioLink !== undefined ||
            game.awayTeam.radioLinkFr !== undefined,
        [game]
    );

    return (
        <>
            <GameInfo>
                <DateNetworkContainer
                    ref={dateContainerRef}
                    $showingBroadcasters={!CoreUtils.isPostGame(game.gameState) && game.tvBroadcasts?.length > 0}
                    $linkContainerProps={linkContainerProps}
                >
                    {!CoreUtils.isPostGame(game.gameState) && (
                        <NetworkContainer>
                            <TvBroadcast
                                tvBroadcasts={game.tvBroadcasts}
                                location={countryCode}
                                pageName={PageName.GameCenter}
                            />
                        </NetworkContainer>
                    )}
                </DateNetworkContainer>
                {game.specialEvent && isMedium && (
                    <SpecialEventDisplay game={game} twoLinksPresent={linkContainerProps.twoLinks} />
                )}
                <LinksContainer ref={linkContainerRef} $linkContainerProps={linkContainerProps}>
                    <LinkContainer>
                        {showTicketsLink(game) && (
                            <ScoreboardButton href={ticketsLink}>
                                <FormattedMessage {...CommonMessages.linkDescriptions.tickets} />
                                <TicketsIcon />
                            </ScoreboardButton>
                        )}
                    </LinkContainer>
                    {isLiveGame && streamUrl && (
                        <LinkContainer>
                            <ScoreboardButton href={streamUrl}>
                                <FormattedMessage {...CommonMessages.linkDescriptions.stream} /> <OpenInNewIcon />
                            </ScoreboardButton>
                        </LinkContainer>
                    )}
                    {CoreUtils.isGameActive(game.gameState) && gameHasRadioStreams && (
                        <LinkContainer>
                            <ScoreboardButton onClick={() => setRadioPlayerOpen(true)}>
                                <FormattedMessage {...CommonMessages.linkDescriptions.listen} />
                                <HeadphonesIcon />
                            </ScoreboardButton>
                        </LinkContainer>
                    )}
                </LinksContainer>
            </GameInfo>
            {game.specialEvent && !isMedium && (
                <SpecialEventDisplay game={game} twoLinksPresent={linkContainerProps.twoLinks} addMargin />
            )}
            <GameOrSeriesScoreboard game={game} leftTeam={game.awayTeam} rightTeam={game.homeTeam}>
                <ScoreBox>
                    <ScoreContainer $isLosingScore={postGame && game.awayTeam.score < game.homeTeam.score}>
                        {game.awayTeam.score}
                    </ScoreContainer>
                    <PeriodTimeContainer>
                        <GameClock
                            game={game}
                            timezone={TimeZoneEnums.AMERICA_NEW_YORK}
                            flexDirection={GameClockStyles.FlexDirection.vertical}
                            intl={intl}
                        />
                        {!isLiveGame && <DateContainer>{formatDate(game.gameDate)}</DateContainer>}
                        {game.situation !== undefined && (
                            <SituationDescription>
                                <div>
                                    <FormattedMessage
                                        {...CommonMessages.gameSituationDescriptions.long.strength}
                                        values={{
                                            skaterCountA: Math.max(
                                                game.situation.awayTeam.strength,
                                                game.situation.homeTeam.strength
                                            ),
                                            skaterCountB: Math.min(
                                                game.situation.awayTeam.strength,
                                                game.situation.homeTeam.strength
                                            ),
                                        }}
                                    />
                                </div>
                                {game.situation.timeRemaining &&
                                    game.situation.awayTeam.strength === game.situation.homeTeam.strength && (
                                        <div>{game.situation.timeRemaining}</div>
                                    )}
                            </SituationDescription>
                        )}
                    </PeriodTimeContainer>
                    <ScoreContainer $isLosingScore={postGame && game.awayTeam.score > game.homeTeam.score}>
                        {game.homeTeam.score}
                    </ScoreContainer>
                </ScoreBox>
            </GameOrSeriesScoreboard>
            {isRadioPlayerOpen &&
                createPortal(
                    <RadioPlayer
                        homeTeam={game.homeTeam}
                        awayTeam={game.awayTeam}
                        closeRadioPlayer={() => setRadioPlayerOpen(false)}
                    />,
                    document.body
                )}
        </>
    );
};

export default Scoreboard;
